import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import {
  Gauge, ArrowUp, ArrowDown, PiggyBank, TrendingUp, TrendingDown, Target, AlertTriangle, Lightbulb, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { formatCurrency, isAtrasado } from '../utils/format';

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const PERIODS = [
  { id: '1', label: 'Este mês', n: 1 },
  { id: '3', label: '3 meses', n: 3 },
  { id: '6', label: '6 meses', n: 6 },
  { id: '12', label: '12 meses', n: 12 },
];

const pad = (n) => String(n).padStart(2, '0');
const keyOf = (y, m) => `${y}-${pad(m + 1)}`;

// Últimas `n` chaves de mês (AAAA-MM) terminando em `offset` meses antes do atual
function monthKeys(n, offset = 0) {
  const base = new Date();
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(base.getFullYear(), base.getMonth() - offset - (n - 1 - i), 1);
    return keyOf(d.getFullYear(), d.getMonth());
  });
}

const pctChange = (cur, prev) => (prev > 0 ? ((cur - prev) / prev) * 100 : null);

function Delta({ value, goodWhenUp = true, suffix = '%' }) {
  if (value == null || !Number.isFinite(value)) return <span className="text-[11px] text-slate-400">sem base de comparação</span>;
  const up = value >= 0;
  const good = up === goodWhenUp;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-bold ${good ? 'text-emerald-500' : 'text-rose-500'}`}>
      <Icon size={12} />
      {up ? '+' : ''}{Math.abs(value) >= 10 ? value.toFixed(0) : value.toFixed(1)}{suffix} vs período anterior
    </span>
  );
}

function ScoreRing({ score, color }) {
  const r = 52; const c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 120 120" className="w-32 h-32 flex-shrink-0">
      <circle cx="60" cy="60" r={r} fill="none" strokeWidth="10" className="stroke-slate-200 dark:stroke-white/10" />
      <circle
        cx="60" cy="60" r={r} fill="none" strokeWidth="10" strokeLinecap="round"
        stroke={color} strokeDasharray={c} strokeDashoffset={c * (1 - score / 100)}
        transform="rotate(-90 60 60)"
      />
      <text x="60" y="58" textAnchor="middle" className="fill-slate-800 dark:fill-white" fontSize="26" fontWeight="800" fontFamily="Plus Jakarta Sans, sans-serif">{score}</text>
      <text x="60" y="76" textAnchor="middle" className="fill-slate-400" fontSize="9" fontWeight="700">DE 100</text>
    </svg>
  );
}

export default function Desempenho() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const [period, setPeriod] = useState('3');
  const [lancamentos, setLancamentos] = useState([]);
  const [metas, setMetas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.all([api.get('/lancamentos'), api.get('/metas/progresso?status=ativa').catch(() => ({ data: [] }))])
      .then(([l, m]) => { if (alive) { setLancamentos(l.data || []); setMetas(m.data || []); } })
      .catch(() => toast.error('Erro ao carregar seu desempenho.'))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const n = PERIODS.find(p => p.id === period).n;

  const data = useMemo(() => {
    // Só o que já foi pago/recebido conta (pendentes ficam de fora, como no dashboard)
    const real = lancamentos.filter(l =>
      (l.status === 'pago' || l.status === 'recebido') && !l.ajusteSaldo && ['RECEITA', 'DESPESA', 'CUSTO'].includes(l.tipo));
    const byMonth = {};
    real.forEach((l) => {
      const mk = (l.dataCompetencia || '').slice(0, 7);
      if (!mk) return;
      byMonth[mk] = byMonth[mk] || { rec: 0, desp: 0 };
      if (l.tipo === 'RECEITA') byMonth[mk].rec += Number(l.valor) || 0; else byMonth[mk].desp += Number(l.valor) || 0;
    });
    const sum = (keys) => keys.reduce((a, k) => ({ rec: a.rec + (byMonth[k]?.rec || 0), desp: a.desp + (byMonth[k]?.desp || 0) }), { rec: 0, desp: 0 });

    const curKeys = monthKeys(n);
    const prevKeys = monthKeys(n, n);
    const cur = sum(curKeys); const prev = sum(prevKeys);
    const res = cur.rec - cur.desp; const resPrev = prev.rec - prev.desp;
    const taxa = cur.rec > 0 ? (res / cur.rec) * 100 : null;
    const taxaPrev = prev.rec > 0 ? (resPrev / prev.rec) * 100 : null;

    const chartKeys = n === 1 ? monthKeys(6) : curKeys;
    const chart = chartKeys.map((k) => ({
      mes: MESES[Number(k.slice(5)) - 1],
      Recebido: Math.round((byMonth[k]?.rec || 0) * 100) / 100,
      Pago: Math.round((byMonth[k]?.desp || 0) * 100) / 100,
      Resultado: Math.round(((byMonth[k]?.rec || 0) - (byMonth[k]?.desp || 0)) * 100) / 100,
    }));

    const catSum = (keys) => {
      const set = new Set(keys); const m = {};
      real.filter(l => l.tipo !== 'RECEITA' && set.has((l.dataCompetencia || '').slice(0, 7)))
        .forEach(l => { const c = l.categoria || 'Sem categoria'; m[c] = (m[c] || 0) + (Number(l.valor) || 0); });
      return m;
    };
    const catCur = catSum(curKeys); const catPrev = catSum(prevKeys);
    const totalDesp = Object.values(catCur).reduce((a, b) => a + b, 0);
    const categorias = Object.entries(catCur).sort((a, b) => b[1] - a[1]).slice(0, 6)
      .map(([nome, valor]) => ({ nome, valor, pct: totalDesp > 0 ? (valor / totalDesp) * 100 : 0, delta: pctChange(valor, catPrev[nome] || 0) }));

    const atrasados = lancamentos.filter(l => l.tipo !== 'TRANSFERENCIA' && isAtrasado(l));
    const totalAtrasado = atrasados.reduce((s, l) => s + (Number(l.valor) || 0), 0);

    // Pontuação simples e transparente (explicada na tela)
    let pontos = 0;
    if (taxa == null) pontos += 25; else if (taxa >= 20) pontos += 60; else if (taxa >= 10) pontos += 45; else if (taxa >= 0) pontos += 30; else pontos += 10;
    pontos += atrasados.length === 0 ? 25 : atrasados.length <= 2 ? 15 : 5;
    if (metas.length === 0) pontos += 10;
    else { const media = metas.reduce((s, m) => s + Math.min(100, m.percentual || 0), 0) / metas.length; pontos += media >= 50 ? 15 : 8; }
    const score = Math.min(100, pontos);

    return { cur, prev, res, resPrev, taxa, taxaPrev, chart, categorias, atrasados, totalAtrasado, score, totalDesp };
  }, [lancamentos, metas, n]);

  const nivel = data.score >= 80 ? { t: 'Excelente', c: '#10B981' } : data.score >= 60 ? { t: 'Boa', c: '#6366F1' } : data.score >= 40 ? { t: 'Atenção', c: '#F59E0B' } : { t: 'Crítica', c: '#EF4444' };

  const insights = useMemo(() => {
    const list = [];
    if (data.taxa != null) {
      if (data.taxa >= 20) list.push({ icon: PiggyBank, tone: 'text-emerald-500', text: `Você guardou ${data.taxa.toFixed(0)}% do que recebeu neste período. Ótimo ritmo.` });
      else if (data.taxa >= 0) list.push({ icon: PiggyBank, tone: 'text-amber-500', text: `Você guardou ${data.taxa.toFixed(0)}% do que recebeu. Um bom objetivo é chegar a 20%.` });
      else list.push({ icon: AlertTriangle, tone: 'text-rose-500', text: `Você gastou mais do que recebeu neste período (${formatCurrency(Math.abs(data.res))} a mais).` });
    } else list.push({ icon: Info, tone: 'text-slate-400', text: 'Ainda não há receitas recebidas neste período para calcular quanto você guardou.' });
    const top = data.categorias[0];
    if (top) list.push({ icon: Lightbulb, tone: 'text-indigo-500', text: `Maior gasto: ${top.nome}, com ${top.pct.toFixed(0)}% das saídas (${formatCurrency(top.valor)}).` });
    const dd = pctChange(data.cur.desp, data.prev.desp);
    if (dd != null) list.push({ icon: dd > 0 ? TrendingUp : TrendingDown, tone: dd > 0 ? 'text-rose-500' : 'text-emerald-500', text: `Seus gastos ${dd > 0 ? 'subiram' : 'caíram'} ${Math.abs(dd).toFixed(0)}% em relação ao período anterior.` });
    if (data.atrasados.length > 0) list.push({ icon: AlertTriangle, tone: 'text-rose-500', text: `${data.atrasados.length} conta${data.atrasados.length > 1 ? 's' : ''} em atraso, somando ${formatCurrency(data.totalAtrasado)}.` });
    return list;
  }, [data]);

  const kpis = [
    { label: 'Recebido', value: formatCurrency(data.cur.rec), icon: ArrowUp, color: '#10B981', delta: <Delta value={pctChange(data.cur.rec, data.prev.rec)} /> },
    { label: 'Pago', value: formatCurrency(data.cur.desp), icon: ArrowDown, color: '#EF4444', delta: <Delta value={pctChange(data.cur.desp, data.prev.desp)} goodWhenUp={false} /> },
    { label: 'Resultado', value: formatCurrency(data.res), icon: TrendingUp, color: '#6366F1', delta: <Delta value={data.resPrev !== 0 ? pctChange(data.res, Math.abs(data.resPrev)) : null} /> },
    { label: 'Quanto você guardou', value: data.taxa == null ? '—' : `${data.taxa.toFixed(1)}%`, icon: PiggyBank, color: '#F59E0B', delta: <Delta value={data.taxa != null && data.taxaPrev != null ? data.taxa - data.taxaPrev : null} suffix=" p.p." /> },
  ];

  return (
    <div className="w-full max-w-6xl mx-auto space-y-5 page-enter pb-16" data-testid="desempenho-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2.5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            <span className="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shadow-sm"><Gauge size={20} /></span>
            Meu Desempenho
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">Como está a sua saúde financeira, com base no que já foi pago e recebido</p>
        </div>
        <div className="flex items-center gap-1 p-1 rounded-2xl bg-slate-100/80 dark:bg-white/5 border border-slate-200/70 dark:border-white/10 self-start">
          {PERIODS.map(p => (
            <button
              key={p.id} onClick={() => setPeriod(p.id)} data-testid={`desempenho-periodo-${p.id}`}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${period === p.id ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-300 shadow-sm' : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'}`}
            >{p.label}</button>
          ))}
        </div>
      </div>

      {/* Pontuação + indicadores */}
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
        <div className="fin-card p-5 flex items-center gap-4" data-testid="desempenho-score">
          <ScoreRing score={loading ? 0 : data.score} color={nivel.c} />
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Saúde financeira</p>
            <p className="text-xl font-extrabold" style={{ color: nivel.c }}>{loading ? '…' : nivel.t}</p>
            <p className="text-[11px] text-slate-400 mt-1 leading-snug">
              Soma quanto você guarda do que recebe (até 60), contas em dia (até 25) e progresso das metas (até 15).
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {kpis.map(({ label, value, icon: Icon, color, delta }) => (
            <div key={label} className="fin-card p-4">
              <div className="flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0" style={{ background: color }}><Icon size={15} /></span>
                <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">{label}</span>
              </div>
              <p className="mt-2 text-lg sm:text-xl font-bold text-slate-800 dark:text-slate-100 truncate">{loading ? '…' : value}</p>
              <div className="mt-0.5">{loading ? null : delta}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Evolução */}
      <div className="fin-card p-4 sm:p-5">
        <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-3">Recebido x pago por mês</h2>
        <div className="h-[240px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data.chart} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#94A3B833" vertical={false} />
              <XAxis dataKey="mes" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#94A3B8' }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#94A3B8' }} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}K` : v)} />
              <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }} />
              <Bar dataKey="Recebido" fill="#10B981" radius={[6, 6, 0, 0]} maxBarSize={28} />
              <Bar dataKey="Pago" fill="#EF4444" radius={[6, 6, 0, 0]} maxBarSize={28} />
              <Line type="monotone" dataKey="Resultado" stroke="#6366F1" strokeWidth={2.5} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="flex flex-wrap gap-4 mt-2 text-[11px] text-slate-500 dark:text-slate-400">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />Recebido</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-rose-500" />Pago</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-indigo-500" />Resultado (recebido − pago)</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Categorias */}
        <div className="fin-card p-4 sm:p-5" data-testid="desempenho-categorias">
          <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-3">Para onde foi o dinheiro</h2>
          {data.categorias.length === 0 ? (
            <p className="text-xs text-slate-400 py-6 text-center">Sem saídas pagas neste período.</p>
          ) : (
            <ul className="space-y-3">
              {data.categorias.map((c) => (
                <li key={c.nome}>
                  <div className="flex items-center justify-between gap-2 text-xs">
                    <span className="font-semibold text-slate-700 dark:text-slate-200 truncate">{c.nome}</span>
                    <span className="text-slate-500 dark:text-slate-400 whitespace-nowrap">{formatCurrency(c.valor)} · {c.pct.toFixed(0)}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 dark:bg-white/10 mt-1 overflow-hidden">
                    <div className="h-full rounded-full bg-indigo-500" style={{ width: `${Math.max(3, c.pct)}%` }} />
                  </div>
                  <div className="mt-0.5"><Delta value={c.delta} goodWhenUp={false} /></div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="space-y-4">
          {/* Destaques */}
          <div className="fin-card p-4 sm:p-5" data-testid="desempenho-insights">
            <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-3">Destaques</h2>
            <ul className="space-y-2.5">
              {insights.map((it, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[13px] text-slate-600 dark:text-slate-300">
                  <it.icon size={16} className={`mt-0.5 flex-shrink-0 ${it.tone}`} />
                  <span>{it.text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Metas */}
          <div className="fin-card p-4 sm:p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2"><Target size={15} className="text-indigo-500" />Metas ativas</h2>
              <button onClick={() => navigate(`/${tenantSlug}/metas`)} className="text-xs font-semibold text-indigo-500 hover:underline">Ver todas</button>
            </div>
            {metas.length === 0 ? (
              <p className="text-xs text-slate-400">Nenhuma meta ativa. Crie uma em Metas para acompanhar aqui.</p>
            ) : (
              <ul className="space-y-3">
                {metas.slice(0, 4).map((m) => {
                  const p = Math.min(100, m.percentual || 0);
                  return (
                    <li key={m.id}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-slate-700 dark:text-slate-200 truncate">{m.titulo}</span>
                        <span className="text-slate-500 dark:text-slate-400">{p.toFixed(0)}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 dark:bg-white/10 mt-1 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${Math.max(2, p)}%`, background: p >= 100 ? '#10B981' : '#6366F1' }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
